/**
 * batch-70 app-dashboard (FO-526, spec 226 §3.B7) - "waar in de app".
 *
 * De conventie (vanaf batch 70, bewaakt door `changelog-waar-drift.test.ts`): elke
 * CHANGELOG-sectie heeft een regel
 *
 *     - Waar: /voorraad/besteladvies, portal:/verlof, klant:/werkbonnen
 *
 * met de schermen die zijn toegevoegd of gewijzigd. Een pad zonder voorvoegsel is
 * de backoffice; `portal:` en `klant:` zijn de andere apps. Een wijziging zonder
 * scherm (achtergrond, data, infra) schrijft `Waar: geen` (eventueel met een reden
 * erachter). Vet (`**Waar**:`), backticks en een spatie in plaats van de dubbele
 * punt na de app mogen.
 *
 * Zonder `Waar:` (de historische secties) wordt de plek geschat uit de module van
 * de kop: dat is altijd gemarkeerd (`waarGeschat`), nooit stil.
 */
import { APP_WAAR_ITEM_RE, type AppWaarBron } from '@streep/types'

export interface WaarWaarde {
  /** Genormaliseerde plekken: `/pad` (backoffice), `portal:/pad`, `klant:/pad`. */
  waar: string[]
  /** De sectie zegt expliciet: geen scherm. */
  geen: boolean
  /** Delen die geen geldig pad zijn (de drift-test meldt ze). */
  ongeldig: string[]
}

/** Een regel `Waar: ...` (lijstpunt of losse regel, vet of niet). */
export const WAAR_REGEL_RE = /^\s{0,3}(?:[-*+]\s+)?(?:\*\*|__)?waar(?:\*\*|__)?\s*:\s*(?:\*\*|__)?\s*(.*?)\s*$/i

const GEEN_RE = /^(geen|n\.?\s?v\.?\s?t\.?|nvt|-)(\b|$|\s|\()/i

/** Eén plek normaliseren; null als het geen geldig pad is. */
export function normaliseerWaarItem(ruw: string): string | null {
  let t = ruw.trim().replace(/^[`'"]+|[`'".]+$/g, '').trim()
  if (t === '') return null
  // "portal /verlof", "Portal: /verlof", "backoffice:/x"
  const app = /^(backoffice|portal|klant)\s*:?\s*(\/.*)$/i.exec(t)
  if (app) {
    const naam = (app[1] ?? '').toLowerCase()
    t = naam === 'backoffice' ? (app[2] ?? '') : `${naam}:${app[2] ?? ''}`
  }
  // Query of anker eraf: de plek is het scherm.
  t = t.replace(/[?#].*$/, '')
  if (t.length > 1 && t.endsWith('/') && !t.endsWith(':/')) t = t.slice(0, -1)
  return APP_WAAR_ITEM_RE.test(t) ? t : null
}

/** De waarde achter `Waar:` uitsplitsen. */
export function parseWaarWaarde(waarde: string): WaarWaarde {
  const kaal = waarde.replace(/\*\*|__/g, '').trim()
  if (GEEN_RE.test(kaal)) return { waar: [], geen: true, ongeldig: [] }
  const waar: string[] = []
  const ongeldig: string[] = []
  for (const deel of kaal.split(/[,;]/)) {
    const d = deel.trim()
    if (d === '') continue
    const item = normaliseerWaarItem(d)
    if (item) {
      if (!waar.includes(item)) waar.push(item)
    } else {
      ongeldig.push(d)
    }
  }
  return { waar, geen: false, ongeldig }
}

/**
 * De standaardplek per module, voor de historische secties zonder `Waar:`. Op
 * volgorde: het eerste trefwoord dat in de module (of, zonder module, in het begin
 * van de titel) voorkomt wint. Elke plek is een bestaand scherm (bewaakt door de
 * drift-test `app-dashboard-spec-index.test.ts`).
 */
export const APP_MODULE_STANDAARD_WAAR: ReadonlyArray<{ trefwoorden: readonly string[]; waar: string }> = [
  { trefwoorden: ['verlof', 'vakantiecampagne', 'tvt', 'atv', 'seniorenregeling'], waar: '/verlof' },
  { trefwoorden: ['verzuim', 'ziek'], waar: '/verzuim' },
  { trefwoorden: ['inkoop'], waar: '/inkoop' },
  { trefwoorden: ['factur', 'factuur', 'finqle', 'zenvoices', 'verzamelbon'], waar: '/facturen' },
  { trefwoorden: ['uitrusting', 'kleding', 'pbm'], waar: '/uitrusting' },
  { trefwoorden: ['voorraad', 'magazijn', 'besteladvies', 'samenstel'], waar: '/voorraad' },
  { trefwoorden: ['payroll', 'loonstro', 'verloning', 'salaris'], waar: '/payroll' },
  { trefwoorden: ['klantenportaal', 'klantportaal'], waar: '/klantenportaal' },
  { trefwoorden: ['kalender', 'agenda'], waar: '/kalender' },
  { trefwoorden: ['planbord', 'planning', 'in-te-plannen', 'rooster'], waar: '/planning' },
  { trefwoorden: ['operationele taken', 'taken'], waar: '/operationele-taken' },
  { trefwoorden: ['document', 'bibliotheek', 'briefpapier', 'huisstijl'], waar: '/documenten' },
  { trefwoorden: ['werkbon'], waar: '/werkbonnen' },
  { trefwoorden: ['overeenkomst'], waar: '/overeenkomsten' },
  { trefwoorden: ['offboarding', 'uit dienst'], waar: '/offboarding' },
  { trefwoorden: ['onboarding'], waar: '/onboarding' },
  { trefwoorden: ['nen 3140', 'nen3140'], waar: '/nen3140' },
  { trefwoorden: ['kluis'], waar: '/kluis' },
  { trefwoorden: ['materieel', 'voertuig', 'garage', 'werkplaats', 'tankpas', 'kilometer', 'telefon', 'apparatuur'], waar: '/materieel' },
  { trefwoorden: ['relatie', 'kvk'], waar: '/relaties' },
  { trefwoorden: ['crm', 'contactmoment'], waar: '/crm' },
  { trefwoorden: ['acquisitie', 'targeting'], waar: '/acquisitie' },
  { trefwoorden: ['recruitment', 'sollicit', 'kandida'], waar: '/recruitment' },
  { trefwoorden: ['declaratie'], waar: '/declaraties' },
  { trefwoorden: ['boete'], waar: '/boetes' },
  { trefwoorden: ['certifica', 'opleiding', 'cursus'], waar: '/hrm/certificaten' },
  { trefwoorden: ['medewerker', 'contract', 'hrm', 'gesprek'], waar: '/medewerkers' },
  { trefwoorden: ['uren', 'weekstaat', 'reistijd', 'reisuren'], waar: '/uren' },
  { trefwoorden: ['trends'], waar: '/trends' },
  { trefwoorden: ['export'], waar: '/exports' },
  { trefwoorden: ['import'], waar: '/imports' },
  { trefwoorden: ['veiligheid', 'rie'], waar: '/veiligheid' },
  { trefwoorden: ['kwaliteit', 'controlelijst'], waar: '/kwaliteit' },
  { trefwoorden: ['mail'], waar: '/mail' },
  { trefwoorden: ['campagne', 'kerstborrel', 'bijtelling'], waar: '/campagnes' },
  { trefwoorden: ['actiepunten'], waar: '/instellingen/actiepunten' },
  { trefwoorden: ['rechtenmatrix', 'autorisatie'], waar: '/instellingen/rechtenmatrix' },
  { trefwoorden: ['idee'], waar: '/ideeen' },
  { trefwoorden: ['gereedschap', 'koffer'], waar: '/gereedschap' },
  { trefwoorden: ['leesbevestiging'], waar: '/documenten' },
  { trefwoorden: ['ai-rapportage', 'rapportage'], waar: '/rapportage' },
  { trefwoorden: ['dashboard'], waar: '/dashboard' },
  { trefwoorden: ['btw'], waar: '/exports' },
  { trefwoorden: ['postvak', 'meldingen', 'notificatie'], waar: '/postvak-in' },
  { trefwoorden: ['portal', 'medewerker-app', 'medewerkersapp'], waar: 'portal:/' },
]

function bevatTrefwoord(tekst: string, woord: string): boolean {
  const veilig = woord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`(^|[^a-z0-9])${veilig}`, 'i').test(tekst)
}

/** De standaardplek voor een module (of, zonder module, het begin van de titel); null = onbekend. */
export function standaardWaarVoorModule(module: string | null, titel: string | null = null): string | null {
  const bron = (module ?? (titel ? titel.split(/\s+/).slice(0, 4).join(' ') : '')).toLowerCase()
  if (bron.trim() === '') return null
  for (const regel of APP_MODULE_STANDAARD_WAAR) {
    if (regel.trefwoorden.some((w) => bevatTrefwoord(bron, w))) return regel.waar
  }
  return null
}

export interface WaarBepaling {
  waar: string[]
  bron: AppWaarBron
  geschat: boolean
}

/**
 * De plek van een wijziging uit haar secties: de `Waar:`-regels samen; anders
 * expliciet geen scherm; anders geschat uit de module (gemarkeerd); anders onbekend.
 */
export function bepaalWaar(
  secties: ReadonlyArray<{ waar: readonly string[]; waarGeen: boolean }>,
  module: string | null,
  titel: string | null,
): WaarBepaling {
  const waar: string[] = []
  for (const s of secties) for (const w of s.waar) if (!waar.includes(w)) waar.push(w)
  if (waar.length > 0) return { waar, bron: 'WAAR_REGEL', geschat: false }
  if (secties.some((s) => s.waarGeen)) return { waar: [], bron: 'GEEN_SCHERM', geschat: false }
  const standaard = standaardWaarVoorModule(module, titel)
  if (standaard) return { waar: [standaard], bron: 'MODULE_STANDAARD', geschat: true }
  return { waar: [], bron: 'ONBEKEND', geschat: false }
}
